import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { StyledProps, ColorProps } from '../types/styled-utils';

const HeaderContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  z-index: 100;
  backdrop-filter: blur(10px);
  background: rgba(21, 21, 40, 0.7);
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    padding: 1rem;
  }
`;

const Logo = styled.div`
  font-size: 1.8rem;
  font-weight: 700;
  
  span {
    color: ${(props: StyledProps) => props.theme.colors.primary};
  }
`;

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    display: none;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  color: ${(props: StyledProps) => props.theme.colors.lightText};
  font-size: 1.5rem;
  
  @media (max-width: ${(props: StyledProps) => props.theme.breakpoints.md}) {
    display: block;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  top: 0;
  right: 0;
  width: 100%;
  max-width: 300px;
  height: 100vh;
  background: ${(props: StyledProps) => props.theme.colors.darkBg2};
  z-index: 110;
  padding: 2rem;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const CloseButton = styled.button`
  align-self: flex-end;
  background: none;
  color: ${(props: StyledProps) => props.theme.colors.lightText};
  font-size: 1.5rem;
  margin-bottom: 2rem;
`;

const NavLink = styled.a`
  color: ${(props: StyledProps) => props.theme.colors.secondaryText};
  font-weight: 500;
  transition: color 0.2s ease;
  position: relative;
  padding: 0.5rem 0;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: ${(props: StyledProps) => props.theme.colors.primary};
    transition: width 0.3s ease;
  }
  
  &:hover {
    color: ${(props: StyledProps) => props.theme.colors.lightText};
    
    &:after {
      width: 100%;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
`;

const SocialIcon = styled.a`
  color: ${(props: StyledProps) => props.theme.colors.secondaryText};
  transition: color 0.2s ease;
  
  &:hover {
    color: ${(props: StyledProps) => props.theme.colors.primary};
  }
  
  svg {
    width: 24px;
    height: 24px;
  }
`;

const Header: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };
  
  return (
    <HeaderContainer>
      <Logo>
        <span>6</span>bit
      </Logo>
      
      <Nav>
        <NavLink href="#home">Home</NavLink>
        <NavLink href="#music">Music</NavLink>
        <NavLink href="#terminal">Terminal</NavLink>
        <NavLink href="#about">About</NavLink>
        
        <SocialLinks>
          <SocialIcon 
            href="https://open.spotify.com/artist/6jSs5ecozKPDZ3LNFwlHH7?si=8vxujiP8QZWrALIyWMN6Bg"
            target="_blank"
            aria-label="Spotify"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.371-.721.49-1.101.241-3.021-1.851-6.819-2.271-11.279-1.241-.418.101-.838-.16-.931-.579-.101-.418.16-.838.579-.931 4.901-1.121 9.13-.601 12.491 1.441.371.241.49.721.241 1.069zm1.481-3.291c-.301.46-.921.621-1.381.311-3.461-2.131-8.731-2.751-12.821-1.501-.491.15-1.021-.13-1.171-.621-.15-.491.13-1.021.621-1.171 4.671-1.421 10.471-.721 14.461 1.771.448.301.609.921.291 1.211zm.141-3.401c-4.151-2.471-11.021-2.701-15.001-1.491-.631.191-1.291-.16-1.481-.781-.191-.621.16-1.291.781-1.481 4.581-1.391 12.191-1.121 17.001 1.731.571.331.761 1.081.421 1.661-.331.571-1.081.761-1.721.361z" />
            </svg>
          </SocialIcon>
          
          <SocialIcon 
            href="https://www.youtube.com/channel/UCeYaEjzXGfBKx1uy7-r00jA"
            target="_blank"
            aria-label="YouTube"
          >
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
            </svg>
          </SocialIcon>
        </SocialLinks>
      </Nav>
      
      <MobileMenuButton onClick={toggleMobileMenu}>
        ☰
      </MobileMenuButton>
      
      {mobileMenuOpen && (
        <MobileMenu
          initial={{ x: '100%' }}
          animate={{ x: 0 }}
          exit={{ x: '100%' }}
          transition={{ duration: 0.3 }}
        >
          <CloseButton onClick={toggleMobileMenu}>✕</CloseButton>
          
          <NavLink href="#home" onClick={toggleMobileMenu}>Home</NavLink>
          <NavLink href="#music" onClick={toggleMobileMenu}>Music</NavLink>
          <NavLink href="#terminal" onClick={toggleMobileMenu}>Terminal</NavLink>
          <NavLink href="#about" onClick={toggleMobileMenu}>About</NavLink>
          
          <SocialLinks>
            <SocialIcon 
              href="https://open.spotify.com/artist/6jSs5ecozKPDZ3LNFwlHH7?si=8vxujiP8QZWrALIyWMN6Bg"
              target="_blank"
              aria-label="Spotify"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.371-.721.49-1.101.241-3.021-1.851-6.819-2.271-11.279-1.241-.418.101-.838-.16-.931-.579-.101-.418.16-.838.579-.931 4.901-1.121 9.13-.601 12.491 1.441.371.241.49.721.241 1.069zm1.481-3.291c-.301.46-.921.621-1.381.311-3.461-2.131-8.731-2.751-12.821-1.501-.491.15-1.021-.13-1.171-.621-.15-.491.13-1.021.621-1.171 4.671-1.421 10.471-.721 14.461 1.771.448.301.609.921.291 1.211zm.141-3.401c-4.151-2.471-11.021-2.701-15.001-1.491-.631.191-1.291-.16-1.481-.781-.191-.621.16-1.291.781-1.481 4.581-1.391 12.191-1.121 17.001 1.731.571.331.761 1.081.421 1.661-.331.571-1.081.761-1.721.361z" />
              </svg>
            </SocialIcon>
            
            <SocialIcon 
              href="https://www.youtube.com/channel/UCeYaEjzXGfBKx1uy7-r00jA"
              target="_blank"
              aria-label="YouTube"
            >
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
              </svg>
            </SocialIcon>
          </SocialLinks>
        </MobileMenu>
      )}
    </HeaderContainer>
  );
};

export default Header; 